import React, { useRef, useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const SignatureCapture = ({ ticketId, ticketNumber, isOnline, queueAction, onSignatureSaved }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerType, setSignerType] = useState('customer');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [existingSignature, setExistingSignature] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const lastPos = useRef(null);

  useEffect(() => {
    if (ticketId && isOnline) {
      fetchExistingSignature();
    } else {
      setLoadingExisting(false);
    }
  }, [ticketId, isOnline]);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const fetchExistingSignature = async () => {
    try {
      const { data } = await supabase?.from('ticket_signatures')
        ?.select('*')
        ?.eq('ticket_id', ticketId)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();
      if (data) setExistingSignature(data);
    } catch (err) {
      // No existing signature
    } finally {
      setLoadingExisting(false);
    }
  };

  const getPos = (e, canvas) => {
    const rect = canvas?.getBoundingClientRect();
    const scaleX = canvas?.width / rect?.width;
    const scaleY = canvas?.height / rect?.height;
    if (e?.touches) {
      return {
        x: (e?.touches?.[0]?.clientX - rect?.left) * scaleX,
        y: (e?.touches?.[0]?.clientY - rect?.top) * scaleY
      };
    }
    return {
      x: (e?.clientX - rect?.left) * scaleX,
      y: (e?.clientY - rect?.top) * scaleY
    };
  };

  const startDrawing = useCallback((e) => {
    e?.preventDefault();
    const canvas = canvasRef?.current;
    if (!canvas) return;
    setIsDrawing(true);
    lastPos.current = getPos(e, canvas);
  }, []);

  const draw = useCallback((e) => {
    e?.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    const pos = getPos(e, canvas);
    ctx?.beginPath();
    ctx?.moveTo(lastPos?.current?.x, lastPos?.current?.y);
    ctx?.lineTo(pos?.x, pos?.y);
    ctx?.stroke();
    lastPos.current = pos;
    setHasSignature(true);
  }, [isDrawing]);

  const stopDrawing = useCallback((e) => {
    e?.preventDefault();
    setIsDrawing(false);
    lastPos.current = null;
  }, []);

  const clearSignature = () => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setHasSignature(false);
    setSaved(false);
    setError('');
  };

  const saveSignature = async () => {
    if (!hasSignature) { setError('Please provide a signature.'); return; }
    if (!signerName?.trim()) { setError('Please enter the signer name.'); return; }
    setSaving(true);
    setError('');
    try {
      const canvas = canvasRef?.current;
      const signatureData = canvas?.toDataURL('image/png');
      const payload = {
        ticket_id: ticketId,
        signature_data: signatureData,
        signer_name: signerName?.trim(),
        signer_email: signerEmail?.trim() || null,
        signer_type: signerType,
        captured_at: new Date()?.toISOString()
      };
      if (isOnline) {
        const { error: dbErr } = await supabase?.from('ticket_signatures')?.insert(payload);
        if (dbErr) throw dbErr;
        setSaved(true);
        setExistingSignature({ ...payload, id: 'new' });
        onSignatureSaved?.('Signature captured and saved successfully!');
      } else {
        await queueAction?.('SAVE_SIGNATURE', payload);
        setSaved(true);
        onSignatureSaved?.('Signature saved offline. Will sync when connected.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to save signature.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingExisting) {
    return (
      <div className="p-5 border-b border-border">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          Loading signature...
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 border-b border-border">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="PenLine" size={16} color="var(--color-primary)" />
          Customer Signature
        </h3>
        {existingSignature && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Icon name="CheckCircle" size={12} color="currentColor" />
            Signed
          </span>
        )}
      </div>

      {existingSignature && !saved ? (
        <div className="space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-xl p-3">
            <p className="text-sm text-green-700 font-medium">Signature on file</p>
            <p className="text-xs text-green-600 mt-0.5">Signed by: {existingSignature?.signer_name}</p>
            {existingSignature?.captured_at && (
              <p className="text-xs text-green-600">
                {new Date(existingSignature?.captured_at)?.toLocaleString()}
              </p>
            )}
          </div>
          {existingSignature?.signature_data && (
            <div className="border border-border rounded-xl overflow-hidden bg-white">
              <img
                src={existingSignature?.signature_data}
                alt={`Signature from ${existingSignature?.signer_name}`}
                className="w-full h-24 object-contain"
              />
            </div>
          )}
          <button
            onClick={() => setExistingSignature(null)}
            className="text-xs text-primary hover:underline"
          >
            Capture new signature
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Signer Name *</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e?.target?.value)}
                placeholder="Full name"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Signer Type</label>
              <select
                value={signerType}
                onChange={(e) => setSignerType(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="customer">Customer</option>
                <option value="technician">Technician</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Email (optional)</label>
            <input
              type="email"
              value={signerEmail}
              onChange={(e) => setSignerEmail(e?.target?.value)}
              placeholder="signer@email.com"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div className="relative">
            <div className="text-xs text-muted-foreground mb-1">Sign below:</div>
            <div className="border-2 border-dashed border-border rounded-xl bg-white overflow-hidden relative">
              <canvas
                ref={canvasRef}
                width={600}
                height={160}
                className="w-full touch-none cursor-crosshair"
                style={{ height: '160px' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              {!hasSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-sm text-muted-foreground/50">Sign here</p>
                </div>
              )}
            </div>
            <div className="absolute bottom-2 right-2">
              <div className="w-full border-t border-border/50" style={{ width: '80%', marginLeft: '10%' }} />
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} color="currentColor" />
              {error}
            </p>
          )}

          {saved && (
            <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
              <Icon name="CheckCircle" size={14} color="#16a34a" />
              <p className="text-xs text-green-700">Signature saved successfully!</p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={clearSignature}
              disabled={!hasSignature || saving}
              className="flex-1 py-2 px-3 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
            >
              <Icon name="RotateCcw" size={14} color="currentColor" />
              Clear
            </button>
            <button
              onClick={saveSignature}
              disabled={!hasSignature || saving || saved}
              className="flex-1 py-2 px-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {saving ? (
                <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
              ) : (
                <><Icon name="Save" size={14} color="currentColor" /> Save Signature</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureCapture;
